<script setup lang="ts">
import { resolveComponent } from 'vue';
import type { Image } from 'sanity';

interface Props {
  image?: Image;
  subImage?: Image;
  alignment?: 'left' | 'right';
  loading: 'lazy' | 'eager';
}

const props = withDefaults(defineProps<Props>(), {
  alignment: 'left',
  image: undefined,
  subImage: undefined,
  loading: 'lazy',
});

/** Host app registers BaseImage from @pkg/ui. */
const BaseImage = resolveComponent('BaseImage');
</script>

<template>
  <div
    v-if="props.image != null"
    :class="['c-section-images', {
      'c-section-images--one-image': props.subImage?.asset == null,
      'c-section-images--left': props.alignment === 'left',
      'c-section-images--right': props.alignment === 'right',
    }]"
  >
    <div
      class="inset-0 absolute flex flex-row justify-center desk:justify-end items-center"
    >
      <component
        :is="BaseImage"
        :loading="loading"
        :image="props.image"
        class="c-section-images__image c-section-images__image--main"
        :width="500"
        :height="500"
      />
      <component
        :is="BaseImage"
        v-if="props.subImage != null"
        :image="props.subImage"
        class="c-section-images__image c-section-images__image--sub"
        :width="282"
        :height="282"
      />
    </div>
  </div>
</template>

<style scoped>
.c-section-images {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  --c-section-images-size: 320px;
  --main-image-size: 248px;
  --sub-image-size: 146px;

  @screen desk {
    --c-section-images-size: 588px;
    --main-image-size: 442px;
    --sub-image-size: 282px;
  }

  max-width: var(--c-section-images-size);
  max-height: var(--c-section-images-size);
  width: var(--c-section-images-size);
  height: var(--c-section-images-size);

  &--one-image {
    & .c-section-images__image--main {
      inset: 0;
      --main-image-size: var(--c-section-images-size);
    }
  }

  &--right {
    & .c-section-images__image--main {
      left: unset;
      right: 0;
    }

    & .c-section-images__image--sub {
      right: unset;
      left: 0;
    }
  }

  &__image {
    position: absolute;
    transition: opacity 0.3s ease-in-out;

    &--main {
      bottom: 0;
      left: 0;
      width: var(--main-image-size);
      max-width: var(--main-image-size);
      height: var(--main-image-size);
      max-height: var(--main-image-size);
    }

    &--sub {
      top: 0;
      right: 0;
      width: var(--sub-image-size);
      max-width: var(--sub-image-size);
      height: var(--sub-image-size);
      max-height: var(--sub-image-size);
    }

  }
}
</style>
